import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineBuildingIcon from 'LuxKit/Icons/line/LineBuildingIcon'
import LineHomeIcon from 'LuxKit/Icons/line/LineHomeIcon'

import { trackClientEvent } from 'analytics/analytics'
import type { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import { EmptyArray } from 'lib/array/arrayUtils'
import { setManySearchParamValues } from 'lib/url/searchUrlUtils'
import { SEARCH_VERTICAL_TAB_MESSAGES, type SearchVertical } from './SearchVerticalTabs.config'
import {
  getActiveSearchVerticalTab,
  getSearchParamValuesForSearchVertical,
} from './searchVerticalTabsUtils'

const allSearchVerticalIcon = <LineBedIcon />
const hotelsSearchVerticalIcon = <LineBuildingIcon />
const homesSearchVerticalIcon = <LineHomeIcon />

interface Props {
  availablePropertyTypes?: Array<App.FilterItem>
  distribution?: 'filled' | 'hugged'
  inputsName: string
  selectedPropertyTypes?: Array<string>
  search: string
  onFilterChange: FilterControlChangeHandler
}

function SearchVerticalTabs(props: Props) {
  const {
    availablePropertyTypes = EmptyArray,
    distribution = 'hugged',
    inputsName,
    selectedPropertyTypes = EmptyArray,
    search,
    onFilterChange,
  } = props
  const intl = useIntl()

  const activeTab = useMemo(() => (
    getActiveSearchVerticalTab(selectedPropertyTypes)
  ), [selectedPropertyTypes])

  const allLabel = intl.formatMessage(SEARCH_VERTICAL_TAB_MESSAGES.all)
  const hotelsLabel = intl.formatMessage(SEARCH_VERTICAL_TAB_MESSAGES.hotels)
  const homesLabel = intl.formatMessage(SEARCH_VERTICAL_TAB_MESSAGES.homes)

  const handleValueChange = useCallback((value: SearchVertical) => {
    trackClientEvent({
      subject: `search-vertical-tab-${value}`,
      action: 'clicked',
      category: 'hotels-search-page',
      type: 'interaction',
    })

    const nextSearch = setManySearchParamValues(search, getSearchParamValuesForSearchVertical(value, availablePropertyTypes))

    onFilterChange({ search: nextSearch })
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [availablePropertyTypes, onFilterChange, search])

  return (
    <SegmentedOptionsGroup<SearchVertical>
      inputsName={inputsName}
      distribution={distribution}
      onValueChange={handleValueChange}
    >
      <SegmentedOption
        value="all"
        checked={activeTab === 'all'}
        icon={allSearchVerticalIcon}
        title={allLabel}
      />
      <SegmentedOption
        value="hotels"
        checked={activeTab === 'hotels'}
        icon={hotelsSearchVerticalIcon}
        title={hotelsLabel}
      />
      <SegmentedOption
        value="homes"
        checked={activeTab === 'homes'}
        icon={homesSearchVerticalIcon}
        title={homesLabel}
      />
    </SegmentedOptionsGroup>
  )
}

export default SearchVerticalTabs
